import React from "react";
import { Box, Container, Heading, Text, VStack, HStack, Icon, Badge, Step, StepDescription, StepIcon, StepIndicator, StepNumber, StepSeparator, StepStatus, StepTitle, Stepper, useSteps, Table, Thead, Tbody, Tr, Th, Td, List, ListItem, ListIcon } from "@chakra-ui/react";
import { FaHammer, FaFileContract, FaHardHat, FaKey, FaCheckCircle, FaHome } from "react-icons/fa";
import { brandConfig } from "../../../brandConfig";
import { NavbarWithCallToAction } from "../../../components/chakra/NavbarWithCallToAction/NavbarWithCallToAction";
import { FooterWithFourColumns } from "../../../components/chakra/FooterWithFourColumns/FooterWithFourColumns";
import { SEOHead } from "../../../components/SEOHead";

const NAVY = "#141C49";
const SAND = "#C2A878";
const SAND_LIGHT = "#F5F0E8";

const steps = [
  { title: 'Pre-Approval', description: 'Loan approval & budget confirmation' },
  { title: 'Land Purchase', description: 'First drawdown for land acquisition' },
  { title: 'Base/Frame', description: 'Foundation & structural framing' },
  { title: 'Lock-Up', description: 'Roof, windows, doors installed' },
  { title: 'Fixing', description: 'Internal fit-out & services' },
  { title: 'Completion', description: 'Final inspection & settlement' },
];

const FinanceServicesConstruction: React.FC = () => {
  const { activeStep } = useSteps({
    index: 1,
    count: steps.length,
  });

  return (
    <Box minH="100vh" bg="gray.50">
      <SEOHead
        pagePath="/finance/services/construction"
        fallbackTitle="Construction Loans - One Finance Australia"
        fallbackDescription="Finance your dream home build with progressive drawdown construction loans. Flexible terms and expert construction finance guidance."
      />
      <NavbarWithCallToAction />

      {/* Hero Section */}
      <Box bg={NAVY} color="white" py={20}>
        <Container maxW="container.xl">
          <HStack spacing={12} flexDir={{ base: "column", md: "row" }} align="center">
            <VStack align={{ base: "center", md: "start" }} spacing={4} flex={1} textAlign={{ base: "center", md: "left" }}>
              <Badge bg={SAND} color={NAVY} fontSize="md" px={4} py={2} borderRadius="full">
                Progressive Drawdown Finance
              </Badge>
              <Heading size="2xl" fontWeight="bold">
                Construction Loans
              </Heading>
              <Text fontSize="lg" opacity={0.9}>
                Build your dream home with specialised construction finance. Pay interest only on drawn funds during construction, with payments released at each stage.
              </Text>
            </VStack>
            <Icon as={FaHammer} boxSize={32} opacity={0.6} />
          </HStack>
        </Container>
      </Box>

      {/* Construction Timeline Stepper */}
      <Container maxW="container.xl" py={16}>
        <Heading textAlign="center" mb={12} size="xl">
          Construction Loan Timeline
        </Heading>
        <Stepper index={activeStep} orientation="vertical" height="600px" gap="0" colorScheme="gray">
          {steps.map((step, index) => (
            <Step key={index}>
              <StepIndicator>
                <StepStatus
                  complete={<StepIcon />}
                  incomplete={<StepNumber />}
                  active={<StepNumber />}
                />
              </StepIndicator>

              <Box flexShrink="0">
                <StepTitle>{step.title}</StepTitle>
                <StepDescription>{step.description}</StepDescription>
              </Box>

              <StepSeparator />
            </Step>
          ))}
        </Stepper>
      </Container>

      {/* What's Covered Table */}
      <Box bg="white" py={16}>
        <Container maxW="container.xl">
          <Heading textAlign="center" mb={12} size="xl">
            What Construction Loans Cover
          </Heading>
          <Table variant="simple" size="lg">
            <Thead bg={NAVY}>
              <Tr>
                <Th color="white">Cost Item</Th>
                <Th color="white">Covered</Th>
                <Th color="white" isNumeric>Typical %</Th>
                <Th color="white">Notes</Th>
              </Tr>
            </Thead>
            <Tbody>
              <Tr>
                <Td fontWeight="bold">Land Purchase</Td>
                <Td><Icon as={FaCheckCircle} color={SAND} boxSize={5} /></Td>
                <Td isNumeric>30–50%</Td>
                <Td>First drawdown payment</Td>
              </Tr>
              <Tr bg="gray.50">
                <Td fontWeight="bold">Building Contract</Td>
                <Td><Icon as={FaCheckCircle} color={SAND} boxSize={5} /></Td>
                <Td isNumeric>40–60%</Td>
                <Td>Released in stages</Td>
              </Tr>
              <Tr>
                <Td fontWeight="bold">Site Preparation</Td>
                <Td><Icon as={FaCheckCircle} color={SAND} boxSize={5} /></Td>
                <Td isNumeric>2–5%</Td>
                <Td>Demolition, clearing, levelling</Td>
              </Tr>
              <Tr bg="gray.50">
                <Td fontWeight="bold">Council Fees</Td>
                <Td><Icon as={FaCheckCircle} color={SAND} boxSize={5} /></Td>
                <Td isNumeric>1–2%</Td>
                <Td>Permits and inspections</Td>
              </Tr>
              <Tr>
                <Td fontWeight="bold">Landscaping</Td>
                <Td><Icon as={FaCheckCircle} color={SAND} boxSize={5} /></Td>
                <Td isNumeric>3–5%</Td>
                <Td>Final stage drawdown</Td>
              </Tr>
              <Tr bg="gray.50">
                <Td fontWeight="bold">Driveway & Fencing</Td>
                <Td><Icon as={FaCheckCircle} color={SAND} boxSize={5} /></Td>
                <Td isNumeric>2–4%</Td>
                <Td>Completion works</Td>
              </Tr>
            </Tbody>
          </Table>

          <Box mt={12} p={8} bg={SAND_LIGHT} borderRadius="xl" border="2px solid" borderColor={SAND}>
            <Heading size="md" mb={4}>Interest During Construction</Heading>
            <Text color="gray.700" mb={4}>
              You only pay interest on the funds that have been drawn down, not the full loan amount. This keeps costs manageable during the build phase.
            </Text>
            <HStack spacing={8} flexWrap="wrap">
              <Box>
                <Text fontSize="sm" color="gray.600">Month 1–3 (Land Only)</Text>
                <Text fontSize="2xl" fontWeight="bold" color={NAVY}>$1,250/mo</Text>
              </Box>
              <Box>
                <Text fontSize="sm" color="gray.600">Month 4–8 (50% Complete)</Text>
                <Text fontSize="2xl" fontWeight="bold" color={NAVY}>$2,100/mo</Text>
              </Box>
              <Box>
                <Text fontSize="sm" color="gray.600">After Completion</Text>
                <Text fontSize="2xl" fontWeight="bold" color={NAVY}>$3,200/mo</Text>
              </Box>
            </HStack>
          </Box>
        </Container>
      </Box>

      {/* Requirements */}
      <Container maxW="container.xl" py={16}>
        <Heading textAlign="center" mb={12} size="xl">
          What You'll Need
        </Heading>
        <Box p={10} bg="white" borderRadius="2xl" shadow="lg">
          <List spacing={4}>
            <ListItem>
              <HStack align="start">
                <ListIcon as={FaFileContract} color={SAND} boxSize={6} mt={1} />
                <Box>
                  <Text fontWeight="bold" fontSize="lg">Fixed-Price Building Contract</Text>
                  <Text color="gray.600">Signed contract with licensed builder showing itemised costs</Text>
                </Box>
              </HStack>
            </ListItem>
            <ListItem>
              <HStack align="start">
                <ListIcon as={FaHardHat} color={SAND} boxSize={6} mt={1} />
                <Box>
                  <Text fontWeight="bold" fontSize="lg">Council Approval</Text>
                  <Text color="gray.600">Development application (DA) and construction certificate (CC)</Text>
                </Box>
              </HStack>
            </ListItem>
            <ListItem>
              <HStack align="start">
                <ListIcon as={FaHome} color={SAND} boxSize={6} mt={1} />
                <Box>
                  <Text fontWeight="bold" fontSize="lg">Land Ownership or Contract</Text>
                  <Text color="gray.600">Title deed or contract of sale for land</Text>
                </Box>
              </HStack>
            </ListItem>
            <ListItem>
              <HStack align="start">
                <ListIcon as={FaCheckCircle} color={SAND} boxSize={6} mt={1} />
                <Box>
                  <Text fontWeight="bold" fontSize="lg">10–20% Deposit</Text>
                  <Text color="gray.600">Combined deposit for land and construction costs</Text>
                </Box>
              </HStack>
            </ListItem>
          </List>
        </Box>
      </Container>

      {/* CTA */}
      <Box bg={NAVY} color="white" py={16}>
        <Container maxW="container.xl" textAlign="center">
          <Icon as={FaKey} boxSize={16} mb={6} />
          <Heading size="xl" mb={4}>Ready to Build Your Dream Home?</Heading>
          <Text fontSize="lg" mb={8}>Get pre-approved and start planning your construction project</Text>
          <HStack justify="center" spacing={4}>
            <Box as="button" px={8} py={3} bg={SAND} color={NAVY} borderRadius="lg" fontWeight="bold">
              Get Pre-Approval
            </Box>
            <Box as="button" px={8} py={3} bg="transparent" border="2px solid" borderColor="white" borderRadius="lg" fontWeight="bold">
              Download Guide
            </Box>
          </HStack>
        </Container>
      </Box>

      <FooterWithFourColumns />
    </Box>
  );
};

export default FinanceServicesConstruction;
